import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarPlus, Info, Coffee, AlertTriangle, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useIsNarrow, useIsMobile } from "@/components/shared/PagePrimitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { storage } from "@/lib/storage";
import { MonthHero, StatementBar, Statement, MiniMonth, ByProject, projectsOf, buildEntries } from "@/components/absences/WorkerStatement";
import { PhotoThumb } from "@/components/shared/PhotoLightbox";
import { AbsenceDialog } from "@/components/absences/AbsenceDialog";
import { TimeGrid } from "@/components/tasks/calendar/TimeGrid";
import { CalToolbar } from "@/components/tasks/calendar/TaskCalendar";
import { TONE, DOW_LONG, MONTHS_GEN, absenceRange, absenceLabel, absenceTone, isPartial, fmtH, hm, hourOf } from "@/lib/taskCalendar";
import { DOW, toneOf, todayIso, monthStart, monthEnd, addMonths, addDays, dateOf, isoOf, monthLabel, daysWord, STATUS_LABEL, isWorkday, holidayOf } from "@/lib/absences";
import { formatHours } from "@/lib/format";
import ShiftEndFixDialog, { needsEndFix } from "@/components/shared/ShiftEndFixDialog";

const STATUS_TONE = {
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  approved: "bg-success/15 text-success",
  rejected: "bg-destructive/10 text-destructive",
};

// Směna v kalendáři: schválená zeleně, čekající oranžově, bez odhlášení červeně, běžící modře.
const SHIFT = {
  approved: { label: "Schváleno", dot: "bg-success", block: "bg-success/10 text-success border-success/30" },
  pending: { label: "Čeká na schválení", dot: "bg-amber-500", block: "bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/35" },
  auto: { label: "Nebyl odhlášen", dot: "bg-destructive", block: "bg-destructive/10 text-destructive border-destructive/30" },
  open: { label: "V práci", dot: "bg-blue-500", block: "bg-blue-500/10 text-blue-800 dark:text-blue-200 border-blue-500/30" },
};
const shiftKind = (r) => (!r.check_out ? "open" : r.approval_status === "approved" ? "approved" : r.auto_closed ? "auto" : "pending");
const hrs = (h) => formatHours(h).replace(" h", " h");
const ABS_LEGEND = [["leave", "Dovolená"], ["sick", "Nemoc · lékař"], ["off", "Volno"]];

function Legend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 justify-end" data-testid="att-cal-legend">
      {["approved", "pending", "auto"].map((k) => <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap"><span className={`w-2 h-2 rounded-full ${SHIFT[k].dot}`} />{SHIFT[k].label}</span>)}
      {ABS_LEGEND.map(([t, l]) => <span key={t} className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap"><span className="w-5 h-3 rounded-full" style={{ background: TONE[t].hatch }} />{l}</span>)}
      <div className="relative">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Vysvětlivky" className="w-6 h-6 rounded-full border border-border bg-card text-muted-foreground grid place-items-center" data-testid="att-cal-info"><Info size={13} /></button>
        {open && <>
          <div onClick={() => setOpen(false)} className="fixed inset-0 z-40" />
          <div className="absolute top-8 right-0 z-50 w-[280px] max-w-[calc(100vw-32px)] bg-card border border-border rounded-[14px] shadow-lg p-3 flex flex-col gap-1.5 text-[13px]">
            <span>Klepněte na den — {""}<b>vpravo</b> (na telefonu pod kalendářem) uvidíte celý den: kde jste byli, kolik hodin a jestli je schválené.</span>
            <span className="text-muted-foreground">Šrafovaně = nepřítomnost, čárkovaný rámeček = žádost čeká na schválení.</span>
            {ABS_LEGEND.map(([t, l]) => <div key={t} className="flex items-center gap-2.5"><span className="w-6 h-4 rounded-full" style={{ background: TONE[t].hatch }} />{l}</div>)}
          </div>
        </>}
      </div>
    </div>
  );
}

// Docházka dělníka (246): stejný kalendář jako v Úkolech — měsíc vlevo, celý vybraný den vpravo
// (na užší obrazovce pod sebou). Den ukáže směny, nepřítomnost a rozvrh; odtud se hlásí nepřítomnost.
export default function WorkerAbsence({ selfApproved = false }) {
  const { user } = useAuth();
  const narrow = useIsNarrow();
  const today = todayIso();
  const [anchor, setAnchor] = useState(today);
  const [sel, setSel] = useState(today);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [bal, setBal] = useState(null);
  const [dialog, setDialog] = useState(false);
  const month = anchor.slice(0, 7);
  // 415: Výpis / Kalendář a Výpis po (pamatuje se), Filtr zakázky, zvýraznění dnů bez záznamu
  const mobile = useIsMobile();
  const [view, setViewS] = useState(() => (storage.get("worker_att_view", "vypis") === "kal" ? "kal" : "vypis"));
  const setView = (v) => { setViewS(v); storage.set("worker_att_view", v); };
  const [group, setGroupS] = useState(() => { const g = storage.get("worker_att_group", "tyden"); return ["den", "tyden", "mesic"].includes(g) ? g : "tyden"; });
  const setGroup = (g) => { setGroupS(g); storage.set("worker_att_group", g); };
  const [pf, setPf] = useState(null);
  const [hi, setHi] = useState(false);
  const [me, setMe] = useState(null);
  const [year, setYear] = useState(null);
  const [daySheet, setDaySheet] = useState(false);

  const load = useCallback(() => {
    api.get("/absences").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    api.get("/absences/balance").then((r) => setBal(r.data)).catch(() => setBal(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const loadRows = useCallback(() => { api.get(`/attendance/monthly?month=${month}`).then((r) => setRows(r.data.rows || [])).catch(() => setRows([])); }, [month]);
  useEffect(() => { loadRows(); }, [loadRows]);
  const loadMe = useCallback(() => { api.get(`/absences/me?month=${month}`).then((r) => setMe(r.data)).catch(() => setMe(null)); }, [month]);
  useEffect(() => { loadMe(); setPf(null); setHi(false); }, [loadMe]);
  const yr = anchor.slice(0, 4);
  useEffect(() => {
    if (group !== "mesic" || view !== "vypis") return;
    api.get(`/absences/me/year?year=${yr}`).then((r) => setYear(r.data)).catch(() => setYear({ year: Number(yr), months: [] }));
  }, [group, view, yr]);
  const [fixRow, setFixRow] = useState(null);   // 254: doplnit konec neodhlášené směny

  const from = monthStart(anchor), to = monthEnd(anchor);
  const cells = useMemo(() => {
    const lead = (dateOf(from).getDay() + 6) % 7;
    const n = Math.ceil((lead + dateOf(to).getDate()) / 7) * 7;
    return Array.from({ length: n }, (_, i) => addDays(from, i - lead));
  }, [from, to]);
  const shiftsByDay = useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (pf && (r.project_id || "—") !== pf) return; const k = isoOf(new Date(r.check_in)); (m[k] = m[k] || []).push(r); });
    return m;
  }, [rows, pf]);
  const onDay = (iso) => items.find((a) => a.status !== "rejected" && a.status !== "cancelled" && a.date_from <= iso && iso <= a.date_to
    && !(a.part === "full" && !isWorkday(iso)));
  const total = rows.reduce((s, r) => s + (r.hours || 0), 0);

  const projects = useMemo(() => projectsOf(rows), [rows]);
  const entries = buildEntries({ from, to, today, rows, onDay, cells: me?.cells, pf });
  const pickRow = (iso) => { setSel(iso); if (narrow) setDaySheet(true); };
  const toggleHi = () => { const m = Object.keys(me?.cells || {}).filter((k) => me.cells[k].missing).sort()[0]; setHi((v) => !v); if (!hi && m) setSel(m); };
  const openMonth = (iso) => { setAnchor(iso); setSel(iso.slice(0, 7) === today.slice(0, 7) ? today : iso); setGroup("tyden"); };
  const go = (n) => { const a = addMonths(anchor, n); setAnchor(a); setSel(a.slice(0, 7) === today.slice(0, 7) ? today : a); };
  const pick = (iso) => { setSel(iso); if (iso.slice(0, 7) !== month) setAnchor(iso); };
  const cancel = async (a) => {
    try { await api.delete(`/absences/${a.id}`); toast.success("Žádost stažena."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Vybraný den
  const sd = dateOf(sel);
  const dayShifts = shiftsByDay[sel] || [];
  const dayAbs = onDay(sel);
  const dayHours = dayShifts.reduce((s, r) => s + (r.hours || 0), 0);
  const hol = holidayOf(sel);
  const sub = [dayShifts.length ? `${hrs(dayHours)} · ${[...new Set(dayShifts.map((r) => r.project))].join(", ")}` : null,
    dayAbs ? `${dayAbs.type_label}${dayAbs.status === "pending" ? " · čeká" : ""}` : null,
    !dayShifts.length && !dayAbs ? (hol || (isWorkday(sel) ? "Bez záznamu" : "Víkend")) : null].filter(Boolean).join(" · ");
  const now = new Date();
  const blocks = dayShifts.map((r) => {
    const a = new Date(r.check_in), b = r.check_out ? new Date(r.check_out) : (sel === today ? now : new Date(a.getTime() + 30 * 60000));
    const k = shiftKind(r);
    return { key: r.id, from: hourOf(a), to: Math.max(hourOf(a) + 0.25, hourOf(b)), lane: 0, lanes: 1, title: r.project,
      time: `${hm(a)}–${r.check_out ? hm(b) : "teď"}`, tip: `${r.project} · ${hm(a)}–${r.check_out ? hm(b) : "teď"}`, className: SHIFT[k].block };
  });
  const absBlocks = dayAbs ? (() => { const [a, b] = absenceRange(dayAbs, user); return [{ key: dayAbs.id, from: a, to: b, tone: absenceTone(dayAbs), label: absenceLabel(dayAbs),
    time: isPartial(dayAbs) ? `${fmtH(a)}–${fmtH(b)}` : "celý den" }]; })() : [];
  const span = [...blocks, ...absBlocks];
  const gStart = Math.min(6, ...span.map((x) => Math.floor(x.from)));
  const gEnd = Math.max(18, ...span.map((x) => Math.ceil(x.to)));

  const upcoming = items.filter((a) => a.date_to >= addDays(today, -60)).sort((x, y) => (x.date_from < y.date_from ? 1 : -1)).slice(0, 12);

  // 415: varianta C — nahoře rozdíl proti plánu, Výpis (dny / týdny / měsíce) a Kalendář, Filtr se zakázkou
  const dayInner = (<>
          <div className="px-4 md:px-[18px] pt-4 pb-3 border-b border-border space-y-2.5">
            <div className="min-w-0">
              <div className="text-lg font-extrabold" data-testid="att-day-title">{DOW_LONG[(sd.getDay() + 6) % 7][0].toUpperCase() + DOW_LONG[(sd.getDay() + 6) % 7].slice(1)} {sd.getDate()}. {MONTHS_GEN[sd.getMonth()]}</div>
              <div className="text-[13px] text-muted-foreground truncate">{sub}</div>
            </div>
            <Button variant="outline" onClick={() => { setDaySheet(false); setDialog(true); }} className="h-10 w-full md:w-auto rounded-full font-bold" data-testid="cal-report"><CalendarPlus size={16} /> Nahlásit nepřítomnost</Button>
          </div>

          {dayAbs && (
            <div className="px-4 md:px-[18px] py-3 border-b border-border flex items-center gap-2 flex-wrap" data-testid="cal-picked">
              <span className={`inline-flex items-center h-6 px-2 rounded-full text-[12px] font-bold ${TONE[absenceTone(dayAbs)].tag}`}>{dayAbs.type_label}</span>
              {dayAbs.makeup && <span className="inline-flex items-center h-6 px-2 rounded-full text-[12px] font-bold bg-secondary">↺ napracuji si to</span>}
              <span className="text-sm font-semibold tabular-nums">{dayAbs.when}</span>
              <span className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-bold uppercase ${STATUS_TONE[dayAbs.status] || ""}`}>{STATUS_LABEL[dayAbs.status]}</span>
              {dayAbs.status === "pending" && <button type="button" onClick={() => cancel(dayAbs)} className="ml-auto text-[13px] font-bold text-brand" data-testid="cal-cancel">Stáhnout žádost</button>}
            </div>
          )}

          {!dayShifts.length && !dayAbs && me?.cells?.[sel]?.missing && (
            <div className="px-4 md:px-[18px] py-3 border-b border-border text-[13px] leading-snug" data-testid="att-day-missing">
              <b className="text-destructive">Bez záznamu.</b> Tento den nemáš směnu ani volno — plán s ním počítá {hrs(me.target || 8)}. Měl jsi volno? Nahlas ho. Byl jsi v práci? Řekni stavbyvedoucímu.
            </div>
          )}
          {dayShifts.map((r) => { const k = shiftKind(r); return (
            <div key={r.id} className="px-4 md:px-[18px] py-3 border-b border-border space-y-1" data-testid={`day-shift-${r.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 ${SHIFT[k].dot}`} />
                <span className="font-semibold truncate flex-1 min-w-0">{r.project}</span>
                <span className="font-bold tabular-nums shrink-0">{hrs(r.hours)}</span>
              </div>
              <div className="text-[13px] text-muted-foreground flex items-center gap-x-2 gap-y-0.5 flex-wrap pl-4">
                <span className="tabular-nums">{hm(new Date(r.check_in))}–{r.check_out ? hm(new Date(r.check_out)) : "teď"}</span>
                {r.break_minutes > 0 && <span className="inline-flex items-center gap-1"><Coffee size={12} /> {r.break_minutes} min</span>}
                <span className="font-semibold">{SHIFT[k].label.toLowerCase()}</span>
              </div>
              {(r.auto_closed || r.out_of_radius || r.has_gps === false) && (
                <div className="pl-4 flex flex-wrap gap-1.5">
                  {r.auto_closed && <span className="text-[11.5px] font-bold text-destructive inline-flex items-center gap-1"><AlertTriangle size={12} /> {r.self_declared ? "konec doplněn · čeká na vedoucího" : "směnu ukončil systém"}</span>}
                  {r.out_of_radius && <span className="text-[11.5px] font-bold text-destructive inline-flex items-center gap-1"><AlertTriangle size={12} /> mimo stavbu</span>}
                  {r.has_gps === false && <span className="text-[11.5px] font-semibold text-amber-600 inline-flex items-center gap-1"><AlertTriangle size={12} /> bez GPS</span>}
                </div>
              )}
              {needsEndFix(r) && (
                <div className="pl-4 pt-1">
                  <button type="button" onClick={() => setFixRow(r)} data-testid={`day-fix-${r.id}`}
                    className="h-9 px-3.5 rounded-full bg-destructive text-white text-[13px] font-bold w-full md:w-auto">Doplnit, kdy jsem skončil</button>
                </div>
              )}
              {r.report && <p className="pl-4 text-[13px] line-clamp-3">{r.report}</p>}
              {(r.corrections || []).filter((c) => (c.note || "").trim()).map((c, i) => (
                <div key={i} className="pl-4 text-[12.5px] text-muted-foreground flex items-start gap-1.5" data-testid={`day-correction-${r.id}-${i}`}>
                  <Pencil size={12} className="mt-[3px] shrink-0" /><span>Upraveno ({c.user_name}): {c.note}</span>
                </div>
              ))}
              {(r.photos || []).length > 0 && <div className="pl-4 flex flex-wrap gap-1.5 pt-1">{r.photos.slice(0, 4).map((url, i) => <PhotoThumb key={i} url={url} />)}</div>}
            </div>); })}

          {(span.length > 0 || !narrow) && <div className="p-2.5 md:p-3">
            <TimeGrid cols={[{ label: "Rozvrh dne", blocks, absBlocks }]} days={[sd]} rowH={narrow ? 38 : 42} height={Math.min((gEnd - gStart) * (narrow ? 38 : 42) + 40, narrow ? 420 : 520)} colMin={0}
              start={gStart} end={gEnd} testid="att-day-grid" />
          </div>}
  </>);
  const monthGrid = (
        <div className="bg-card border border-border rounded-[20px] p-2.5 md:p-3.5 min-w-0 basis-full lg:basis-0 lg:grow-[2]" style={{ minWidth: narrow ? 0 : 520 }} data-testid="att-month-grid">
          <div className="grid grid-cols-7 gap-1 md:gap-1.5 mb-1.5">{DOW.map((x) => <div key={x} className="text-center text-[11px] font-bold tracking-[.1em] text-muted-foreground py-1.5">{x}</div>)}</div>
          <div className="grid grid-cols-7 gap-1 md:gap-1.5">
            {cells.map((iso) => {
              const list = shiftsByDay[iso] || [];
              const a = onDay(iso);
              const isSel = iso === sel, isToday = iso === today, inMonth = iso.slice(0, 7) === month;
              const off = !isWorkday(iso);
              const miss = !a && !list.length && !!me?.cells?.[iso]?.missing;   // 415: pracovní den bez docházky i volna
              const h = list.reduce((s, r) => s + (r.hours || 0), 0);
              const worst = list.length ? SHIFT[list.map(shiftKind).sort((x, y) => ["auto", "open", "pending", "approved"].indexOf(x) - ["auto", "open", "pending", "approved"].indexOf(y))[0]] : null;
              return (
                <div key={iso} role="button" tabIndex={0} onClick={() => pick(iso)} onKeyDown={(e) => { if (e.key === "Enter") pick(iso); }} title={holidayOf(iso) || undefined}
                  className={`min-h-[60px] md:min-h-[104px] rounded-[10px] md:rounded-[12px] border p-1 md:p-2 cursor-pointer flex flex-col gap-1 min-w-0
                    ${isSel ? "border-brand ring-2 ring-brand/30" : a?.status === "pending" ? "border-dashed border-brand/70" : miss ? "border-dashed border-destructive/70" : "border-border"}
                    ${a ? "" : isSel ? "bg-brand/10" : miss && hi ? "bg-destructive/10" : off ? "bg-secondary/50" : "bg-card hover:bg-secondary/50"}`}
                  style={{ opacity: inMonth ? 1 : 0.4, background: a ? TONE[absenceTone(a)].hatch : undefined }} data-testid={`cal-${iso}`}>
                  <div className="flex items-center justify-between gap-1 min-w-0">
                    <span className={`text-[12px] md:text-[13px] font-bold ${isToday ? "text-brand" : "text-muted-foreground"}`}>{isToday && <span className="hidden md:inline text-[10.5px] tracking-[.1em] mr-1">DNES</span>}{dateOf(iso).getDate()}</span>
                    {holidayOf(iso) && <span className="hidden xl:block text-[10px] text-muted-foreground truncate">{holidayOf(iso)}</span>}
                  </div>
                  {/* den: hodiny (barva podle nejhoršího stavu směn), pod tím stavby */}
                  {list.length > 0 && (
                    <div className="flex flex-col gap-1 min-w-0" title={list.map((r) => `${r.project} · ${hrs(r.hours)}`).join("\n")}>
                      <span className={`self-start inline-flex items-center gap-1 text-[10.5px] md:text-[12px] font-bold tabular-nums leading-none px-1 md:px-1.5 py-[3px] md:py-1 rounded-md border ${worst.block}`}>
                        <span className={`hidden md:inline w-1.5 h-1.5 rounded-full ${worst.dot}`} />{worst === SHIFT.open ? <><span className="md:hidden">teď</span><span className="hidden md:inline">v práci</span></> : <><span className="md:hidden">{String(Math.round(h * 10) / 10).replace(".", ",")}</span><span className="hidden md:inline">{hrs(h)}</span></>}
                      </span>
                      <div className="hidden md:block"><span className="text-[11px] leading-snug text-muted-foreground line-clamp-2 break-words">{[...new Set(list.map((r) => r.project))].join(" · ")}</span></div>
                    </div>
                  )}
                  {a && <span className={`mt-auto self-start max-w-full truncate text-[10px] md:text-[10.5px] font-bold px-1.5 py-px rounded-full ${TONE[absenceTone(a)].tag}`}>
                    <span className="md:hidden">{a.part === "half" ? "½" : ""}{a.code}{a.makeup ? " ↺" : ""}</span><span className="hidden md:inline">{absenceLabel(a).toLowerCase()}{a.makeup ? " · napracuji" : ""}</span></span>}
                  {miss && <span className="mt-auto self-start text-[10px] md:text-[10.5px] font-bold px-1.5 py-px rounded-full bg-destructive/10 text-destructive" data-testid={`cal-miss-${iso}`}><span className="md:hidden">?</span><span className="hidden md:inline">bez záznamu</span></span>}
                </div>);
            })}
          </div>
        </div>
  );
  const hero = <MonthHero me={me} rows={rows} anchor={from} today={today} pf={pf} projects={projects} hi={hi} onHi={toggleHi} onClearFilter={() => setPf(null)} />;
  const bar = <StatementBar view={view} setView={setView} mobile={mobile} projects={projects} total={total} pf={pf} setPf={setPf} group={group} setGroup={setGroup} />;
  const statement = <Statement entries={entries} group={group} year={year} anchor={from} today={today} target={pf ? null : me?.target} projects={projects}
    sel={sel} hi={hi} onPick={pickRow} onMonth={openMonth} />;

  return (
    <div className="space-y-4" data-testid="worker-absence">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {bal?.entitlement != null ? (
          <span className="inline-flex items-center h-8 px-3 rounded-full bg-card border border-border text-[13px] font-semibold tabular-nums" data-testid="vacation-balance">
            Dovolená {bal.year}: zbývá {daysWord(bal.remaining)} z {bal.entitlement}{bal.pending ? <span className="text-muted-foreground">&nbsp;· čeká {daysWord(bal.pending)}</span> : null}
          </span>
        ) : <span />}
        {view === "kal" ? <Legend /> : null}
      </div>

      <CalToolbar title={monthLabel(from)} onPrev={() => go(-1)} onToday={() => { setAnchor(today); setSel(today); }} onNext={() => go(1)} />

      {!narrow && view === "vypis" ? (
        <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(300px,1fr)] gap-4 items-start" data-testid="att-statement-view">
          <div className="space-y-3 min-w-0">{hero}{bar}{statement}</div>
          <div className="space-y-3 min-w-0 sticky top-4">
            <MiniMonth from={from} to={to} today={today} rows={rows} onDay={onDay} cells={me?.cells} sel={sel} hi={hi} onPick={pickRow} pf={pf} />
            <ByProject projects={projects} pf={pf} setPf={setPf} />
            <div className="bg-card border border-border rounded-[20px] overflow-hidden min-w-0" data-testid="att-day-panel">{dayInner}</div>
          </div>
        </div>
      ) : (
        <>
          {hero}
          {bar}
          {view === "vypis" ? statement : (
            <div className="flex flex-wrap gap-4 items-start" data-testid="att-month-view">
              {monthGrid}
              <div className="bg-card border border-border rounded-[20px] overflow-hidden min-w-0 basis-full lg:basis-0 lg:grow" style={{ minWidth: narrow ? 0 : 300 }} data-testid="att-day-panel">{dayInner}</div>
            </div>
          )}
        </>
      )}

      <div>
        <h2 className="text-lg font-bold mb-2">Moje volno a žádosti</h2>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-card border border-border rounded-[20px] p-4">Zatím žádná nepřítomnost.</p>
        ) : (
          <div className="bg-card border border-border rounded-[20px] divide-y divide-border">
            {upcoming.map((a) => (
              <button key={a.id} type="button" onClick={() => pick(a.date_from)} className="w-full p-3.5 flex items-center gap-3 text-left hover:bg-secondary/50" data-testid={`my-abs-${a.id}`}>
                <span className={`inline-flex items-center justify-center min-w-[34px] h-8 px-1.5 rounded-[8px] text-[12px] font-extrabold ${toneOf(a.code)}`}>{a.code}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold leading-snug">{a.type_label}{a.makeup ? " · napracuji si to" : ""} · <span className="tabular-nums">{a.when}</span></div>
                  {a.status === "rejected" && a.reject_reason ? <div className="text-[13px] text-destructive truncate">Zamítnuto: {a.reject_reason}</div>
                    : a.note ? <div className="text-[13px] text-muted-foreground truncate">„{a.note}"</div> : null}
                </div>
                <span className={`shrink-0 inline-flex items-center h-6 px-2 rounded-full text-[11px] font-bold uppercase ${STATUS_TONE[a.status] || ""}`}>{STATUS_LABEL[a.status]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <AbsenceDialog open={dialog} onClose={() => setDialog(false)} mode={selfApproved ? "self" : "request"}
        initial={{ date_from: sel, date_to: sel }} onSaved={() => { load(); loadMe(); }} />
      <Dialog open={daySheet && narrow && view === "vypis"} onOpenChange={setDaySheet}>
        <DialogContent sheet className="md:max-w-md p-0 overflow-hidden max-md:pt-6" data-testid="att-day-sheet">
          <DialogHeader className="sr-only"><DialogTitle>Vybraný den</DialogTitle><DialogDescription>Směny a volno vybraného dne</DialogDescription></DialogHeader>
          <div className="max-h-[80vh] overflow-y-auto">{dayInner}</div>
        </DialogContent>
      </Dialog>
      <ShiftEndFixDialog row={fixRow} open={!!fixRow} onOpenChange={(o) => { if (!o) setFixRow(null); }} onSaved={() => { loadRows(); loadMe(); }} />
    </div>
  );
}
